import cn from 'classnames';
import React from 'react';
import Button, { ButtonType } from '../Button';
import { useExpandableText } from './hooks';

export interface ExpandableTextProps {
  // Additional style classes.
  className?: string;
  // Collapse text. If provided, text can collapse.
  collapseText?: string;
  // Expand button text. If provided, text can expand.
  expandText?: string;
  // Number of lines to display while collapsed
  numberOfLines: number;
  // Callback when expand button is clicked.
  onClickExpand?: () => void;
  // Callback when collapse button is clicked.
  onClickCollapse?: () => void;
  // Text to display
  text: string;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  className,
  collapseText,
  expandText = 'more',
  numberOfLines,
  onClickExpand,
  onClickCollapse,
  text,
}) => {
  const {
    collapsedHeight,
    containerRef,
    expandedHeight,
    expandedRef,
    expandButtonRef,
    expandTextWidth,
    handleCollapse,
    handleExpand,
    isExpanded,
    lineHeight,
    showExpand,
    singleLineRef,
  } = useExpandableText({
    className,
    collapseText,
    expandText,
    numberOfLines,
    onClickExpand,
    onClickCollapse,
    text,
  });

  // Create expand button with TextLink style
  const expandButton = (
    <Button buttonType={ButtonType.TextLink} onClick={handleExpand}>
      {expandText}
    </Button>
  );

  // Create collapse button with TextLink style
  const collapseButton = collapseText ? (
    <Button buttonType={ButtonType.TextLink} onClick={handleCollapse}>
      {collapseText}
    </Button>
  ) : null;

  // Calculate fade mask gradient - fades from 80% minus button width
  const fadeMaskGradient =
    `linear-gradient(to right, black calc(80% - ${expandTextWidth}px), ` +
    `transparent calc(100% - ${expandTextWidth}px))`;

  // Initial text - shows first N-1 lines when collapsed and truncated
  // Use 'auto' height until measurements are available to prevent invisible text during SSR
  const hasMeasurements = expandedHeight > 0;
  const getHeight = () => {
    if (!hasMeasurements) {
      return 'auto';
    }
    return !showExpand ? expandedHeight : collapsedHeight;
  };

  const initialText = (
    <div
      className='overflow-hidden'
      style={{
        height: getHeight(),
      }}>
      {text}
    </div>
  );

  // Last line container - shows last line with fade effect
  const lastLineContainer = (
    <div className='flex'>
      {/* Last line wrapper with fade mask */}
      <div
        className='h-full overflow-hidden'
        style={{
          height: `${lineHeight}px`,
          WebkitMaskImage: fadeMaskGradient,
          maskImage: fadeMaskGradient,
        }}>
        <div
          className='overflow-hidden'
          style={{
            display: '-webkit-box',
            WebkitLineClamp: numberOfLines,
            WebkitBoxOrient: 'vertical',
            transform: `translateY(-${lineHeight * (numberOfLines - 1)}px)`,
          }}>
          {text}
        </div>
      </div>
    </div>
  );

  // Expand button - positioned absolutely after faded text
  const expandButtonElement = (
    <span ref={expandButtonRef} className='absolute bottom-0 right-0 whitespace-nowrap pl-1'>
      {expandButton}
    </span>
  );

  // Expanded text - shows full text when expanded
  const expandedText = (
    <div className='overflow-hidden'>
      {text}
      {showExpand && collapseButton && <span className='ml-1 inline'>{collapseButton}</span>}
    </div>
  );

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Initial text - shows first N-1 lines when collapsed and truncated */}
      {!isExpanded && initialText}
      {/* Last line container with fade effect - shows last line when collapsed and truncated */}
      {!isExpanded && showExpand && lastLineContainer}
      {/* Expand button - positioned absolutely after faded text */}
      {!isExpanded && showExpand && expandButtonElement}
      {/* Expanded text - shows full text when expanded */}
      {isExpanded && expandedText}
      {/* Hidden measurement elements */}
      <div ref={expandedRef} className='pointer-events-none invisible absolute opacity-0' aria-hidden='true'>
        {text}
      </div>
      <div
        ref={singleLineRef}
        className='pointer-events-none invisible absolute whitespace-nowrap opacity-0'
        aria-hidden='true'>
        {text}
      </div>
    </div>
  );
};
